import type { AdvisorViewStatus } from '@/types/enums/advisor-view-status'
import type { OrderSide } from '@/types/enums/order'
import type { TraderViewStatus } from '@/types/enums/trader-view-status'
import type { OrderBookRowItem, OrderSummaryData } from './types'

import { useEffect } from 'react'

import {
  selectActivePanel,
  selectInitializeWithOrderSide,
  selectIsCollapsed,
  selectIsEmpty,
  selectIsPanelActive,
  selectOrders,
  selectSelectSide,
  selectSetActivePanel,
  selectSetIsCollapsed,
  selectSummaryData,
  selectToggleCollapse,
  selectUpdateCollapseState,
  selectUpdateFromProps,
  useOrderBookStore,
} from './store'

export function useOrderBookState(
  initialOrders: OrderBookRowItem[],
  initialSummaryData: OrderSummaryData,
  initialIsEmpty: boolean,
  status?: TraderViewStatus | AdvisorViewStatus,
  orderSide?: OrderSide.Buy | OrderSide.Sell,
) {
  // Get store state using selectors for optimal performance
  const activePanel = useOrderBookStore(selectActivePanel)
  const isCollapsed = useOrderBookStore(selectIsCollapsed)
  const orders = useOrderBookStore(selectOrders)
  const summaryData = useOrderBookStore(selectSummaryData)
  const isEmpty = useOrderBookStore(selectIsEmpty)

  // Get actions using individual selectors for stable references
  const setActivePanel = useOrderBookStore(selectSetActivePanel)
  const selectSide = useOrderBookStore(selectSelectSide)
  const setIsCollapsed = useOrderBookStore(selectSetIsCollapsed)
  const toggleCollapse = useOrderBookStore(selectToggleCollapse)
  const updateFromProps = useOrderBookStore(selectUpdateFromProps)
  const updateCollapseState = useOrderBookStore(selectUpdateCollapseState)
  const initializeWithOrderSide = useOrderBookStore(
    selectInitializeWithOrderSide,
  )
  const isPanelActive = useOrderBookStore(selectIsPanelActive)

  // Update store when props change
  useEffect(() => {
    updateFromProps(initialOrders, initialSummaryData, initialIsEmpty)
  }, [initialOrders, initialSummaryData, initialIsEmpty, updateFromProps])

  // Update collapse state when status changes
  useEffect(() => {
    updateCollapseState(status)
  }, [status, updateCollapseState])

  // Initialize with order side when it changes
  useEffect(() => {
    initializeWithOrderSide(orderSide)
  }, [orderSide, initializeWithOrderSide])

  return {
    // State
    activePanel,
    isCollapsed,
    orders,
    summaryData,
    isEmpty,

    // Actions
    setActivePanel,
    selectSide,
    setIsCollapsed,
    toggleCollapse,
    isPanelActive,
  }
}
